import dayjs from 'dayjs';

import { createRandomizer, generateTimeSeries, shiftNumber } from '@cast/utils';

// Settings
const MAX_Y_AXIS_VALUE = 0.3;

const DAYS = 8;
const SAMPLES = 3 * DAYS;

export type SimulationMetricPoint = {
  timestamp: string;
  max: number;
  p50: number;
  min: number;
  rec?: number;
};

type GenerateArgs = {
  seed: string;
  /**
   * Recommendation percentile as a fraction (0..1). When undefined, no `rec`
   * line is rendered. The simulation baseline is the generated `max` series.
   */
  percentile?: number;
  /** Overhead as a fraction (0..1). Defaults to 0 when not provided. */
  overhead?: number;
};

/**
 * Pure formula for the simulated recommendation line. Mirrors the BE formula
 * `(1 + overhead) * function(args)` where `function(args)` is the percentile
 * interpolated between `min` and `max` of the current sample.
 *
 * When `percentile` is undefined the function returns undefined so the chart
 * can omit the recommendation line entirely.
 */
export const getSimulatedRecommendation = ({
  min,
  max,
  percentile,
  overhead = 0,
}: {
  min: number;
  max: number;
  percentile?: number;
  overhead?: number;
}): number | undefined => {
  if (percentile === undefined) {
    return undefined;
  }
  const percentileValue = min + (max - min) * percentile;
  return percentileValue * (1 + overhead);
};

/**
 * Generates the deterministic time series used by the WOOP-settings simulation chart.
 *
 * The `rec` line is drawn when `percentile` is provided; it uses the formula
 * `(1 + overhead) * (min + (max - min) * percentile)`. When only `overhead`
 * (and no `percentile`) is passed, no recommendation line is rendered.
 */
export const generateSimulationMetrics = ({
  seed,
  percentile,
  overhead,
}: GenerateArgs): SimulationMetricPoint[] => {
  const random = createRandomizer({ token: seed }).random;
  // Recompute the window on every call so the simulated "current" time tracks
  // real time over a long-lived SPA session (was frozen at module import).
  const to = dayjs().startOf('hour');
  const from = to.subtract(SAMPLES, 'hours');

  const series = generateTimeSeries({
    from,
    to,
    initial: { max: 0.1 },
    nextNumber: (current) =>
      shiftNumber({
        value: current,
        random,
        min: 0.1,
        max: MAX_Y_AXIS_VALUE,
        percentageOffset: 90,
      }),
    unit: 'hour',
  });

  return series.map((item) => {
    const min = item.max * 0.3;
    const max = item.max;
    const p50 = (max + min) / 2;
    const rec = getSimulatedRecommendation({ min, max, percentile, overhead });

    return {
      timestamp: item.timestamp,
      max,
      p50,
      min,
      rec,
    };
  });
};

export const SIMULATION_DAYS = DAYS;
